import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack, Title, Group, Card, Table, Text, Skeleton, Pagination, Button, Badge,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { RoleSelector } from '../../components/RoleSelector.tsx';
import { useVisitors, useMyRoles } from '../../api/hooks/useVisitors.ts';

export function VisitorsListPage() {
  const navigate = useNavigate();
  const myRoles = useMyRoles();

  // Default to the first role the user belongs to.
  const defaultRole = myRoles.data?.[0]?.id ?? null;
  const [role, setRole] = useState<number | null>(defaultRole);
  const [page, setPage] = useState(0);
  const size = 20;

  // Re-sync the default once the roles arrive.
  if (role == null && defaultRole != null) setRole(defaultRole);

  const list = useVisitors({ role: role ?? -1, page, size });

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Title order={2}>Visitors</Title>
        <Group>
          <RoleSelector value={role} onChange={r => { setRole(r); setPage(0); }} />
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/visitors/new')}>
            New visitor
          </Button>
        </Group>
      </Group>

      <Card withBorder padding={0} radius="md">
        {role == null || list.isPending ? (
          <Stack gap="xs" p="md">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} h={28} />)}</Stack>
        ) : list.isError ? (
          <Text p="md" c="red">Failed to load — pick a role to scope the visitors list.</Text>
        ) : (
          <Table.ScrollContainer minWidth={700}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>First name</Table.Th>
                  <Table.Th>Last name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Nationality</Table.Th>
                  <Table.Th>Consent</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.rows.map(v => (
                  <Table.Tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/visitors/${v.id}`)}>
                    <Table.Td>{v.firstName ?? ''}</Table.Td>
                    <Table.Td>{v.lastName ?? ''}</Table.Td>
                    <Table.Td>{v.email ?? ''}</Table.Td>
                    <Table.Td>{v.nationality ?? ''}</Table.Td>
                    <Table.Td>
                      {v.consentReceived
                        ? <Badge color="green" variant="light">Received</Badge>
                        : v.consentRequested
                          ? <Badge color="yellow" variant="light">Requested</Badge>
                          : <Badge color="gray" variant="light">None</Badge>}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {list.data.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">No visitors for this role yet.</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {list.data && list.data.totalCount > size && (
        <Group justify="center">
          <Pagination
            value={page + 1}
            onChange={p => setPage(p - 1)}
            total={Math.max(1, Math.ceil(list.data.totalCount / size))}
          />
          <Text size="sm" c="dimmed">{list.data.totalCount} total</Text>
        </Group>
      )}
    </Stack>
  );
}
